// src/pages/ChatBot/query-logs/inspect-cards.tsx
//
// Query and answer cards for the Inspect modal: what was asked (question,
// standalone rewrite, asker, outcome line) and what was served (custom/chatbot
// answer, user incorrect-flag, error, sources). Derived flag chips render in the
// modal header (InspectModal HeaderTags), beside the status.

import type { QueryLogDoc, QueryOutcome } from './types';
import { formatDateTime, normalizeFeedbackSignal } from './derive';

const OUTCOME_SEPARATOR = ' · ';

function buildOutcomeLine(outcome: QueryOutcome): string {
  const segments = [`Served: ${outcome.served}`];
  if (outcome.generation) segments.push(`generation ${outcome.generation}`);
  if (outcome.refusalReason) {
    segments.push(
      outcome.detail
        ? `refused: ${outcome.refusalReason} (${outcome.detail})`
        : `refused: ${outcome.refusalReason}`,
    );
  } else if (outcome.detail) {
    // Without a refusal, detail is the answer-side finding: a guardrail category
    // (canary-leak / prompt-leak / template-artifact / ungrounded-answer) or 'truncated-answer'.
    segments.push(`flagged: ${outcome.detail}`);
  }
  if (outcome.similarity !== undefined) segments.push(`similarity ${outcome.similarity.toFixed(3)}`);
  if (outcome.matchedBy) segments.push(`matched by ${outcome.matchedBy}`);
  return segments.join(OUTCOME_SEPARATOR);
}

function buildNoAnswerNote(outcome: QueryOutcome): string {
  if (outcome.served === 'refusal') {
    return outcome.refusalReason
      ? `No answer was served — the query was refused (${outcome.refusalReason}).`
      : 'No answer was served — the query was refused.';
  }
  if (outcome.served === 'error') return 'No answer was served — the pipeline errored.';
  if (outcome.served === 'still-learning') {
    return 'No generated answer — the still-learning fallback was served.';
  }
  if (outcome.served === 'manager-fallback') {
    return 'No generated answer — the manager fallback was served.';
  }
  return 'No answer text was stored for this query.';
}

export function QueryCard({ log }: { log: QueryLogDoc }) {
  const hasDifferentStandaloneQuery =
    log.standaloneQuery !== undefined && log.standaloneQuery !== log.question;

  return (
    <div className="qli-card">
      <div className="qli-question">{log.question}</div>
      {hasDifferentStandaloneQuery && (
        <div className="qli-muted-line">Standalone: {log.standaloneQuery}</div>
      )}
      <div className="qli-muted-line">
        {log.askedBy}
        {OUTCOME_SEPARATOR}
        {formatDateTime(log.askedAt)}
      </div>
      <div className="qli-outcome-line">{buildOutcomeLine(log.outcome)}</div>
    </div>
  );
}

export function AnswerCard({ log }: { log: QueryLogDoc }) {
  const hasAnswer = log.customAnswer !== undefined || log.chatbotAnswer !== undefined;
  // incorrectAnswer covers legacy pre-rating docs (implicitly negative).
  const ratedAnswer = log.feedbackAnswer ?? log.incorrectAnswer;
  const ratedLabel =
    normalizeFeedbackSignal(log) === 1
      ? 'User rated this answer positive'
      : 'User rated this answer negative';

  return (
    <div className="qli-card">
      {log.customAnswer && (
        <div className="qli-answer-block">
          <div className="qli-micro-label">Custom answer (overrides pipeline)</div>
          <div className="qli-answer-text">{log.customAnswer.answer}</div>
          <div className="qli-muted-line">
            by {log.customAnswer.answeredBy}
            {OUTCOME_SEPARATOR}
            {formatDateTime(log.customAnswer.answeredAt)}
          </div>
        </div>
      )}
      {log.chatbotAnswer && (
        <div className="qli-answer-block">
          <div className="qli-micro-label">Chatbot answer</div>
          <div className="qli-answer-text">{log.chatbotAnswer}</div>
        </div>
      )}
      {log.blockedAnswer && (
        <div className="qli-error-box">
          <div className="qli-micro-label qli-error-label">
            Blocked answer{log.outcome.detail ? ` — ${log.outcome.detail}` : ''} (never shown to the user)
          </div>
          <div className="qli-answer-text">{log.blockedAnswer}</div>
        </div>
      )}
      {!hasAnswer && <div className="qli-muted-line">{buildNoAnswerNote(log.outcome)}</div>}
      {ratedAnswer && (
        <div className="qli-flagged-box">
          <div className="qli-micro-label qli-flagged-label">{ratedLabel}</div>
          <div className="qli-answer-text">{ratedAnswer}</div>
        </div>
      )}
      {log.errorDescription && (
        <div className="qli-error-box">
          <div className="qli-micro-label qli-error-label">Error</div>
          <div className="qli-answer-text">{log.errorDescription}</div>
        </div>
      )}
      {log.sources && log.sources.length > 0 && (
        <div className="qli-muted-line">Sources: {log.sources.join(', ')}</div>
      )}
    </div>
  );
}
